import { getLogger } from '../services/logger/logger.service'
import type { BasePageOrSubPage, BasePagesEnum } from '../services/models/pagelist-models'
import { Services } from '../services/services'
import { removeSessionStorage } from '../utils/session/removeSessionStorage'
import { setSessionStorage } from '../utils/session/setSessionStorage'
import type { PageValidator } from './models/pageValidator'
import type { WorkflowState } from './models/workflowState'

const logger = getLogger('Workflow')

export enum WorkflowResult {
    Ok,
    SkipBack,
    SkipForward,
    NotValid,
}

export abstract class Workflow<PagesEnum extends BasePageOrSubPage, State extends WorkflowState<PagesEnum>> {
    protected pages: Array<keyof PagesEnum>
    protected stateStorageKey: string
    protected state: State
    protected pagesEnum: PagesEnum

    public constructor(pagesEnumClass: new () => PagesEnum) {
        this.pagesEnum = new pagesEnumClass()
    }

    public get parentAlias() {
        return this.pagesEnum?.parent as keyof PagesEnum | undefined
    }

    public get State() {
        return this.state
    }

    public inWorkflow(page: keyof PagesEnum) {
        const result = this.pages.indexOf(page) >= 0
        if (result) {
            logger.value.trace('inWorkflow', { page, result })
        }

        return result
    }

    public getNextPage(page: keyof PagesEnum): keyof PagesEnum | undefined {
        const pageIndex = this.pages.indexOf(page)
        if (pageIndex === -1 || pageIndex + 1 >= this.pages.length) {
            return undefined
        }

        return this.pages[pageIndex + 1]
    }

    public goToNextPage(page: keyof PagesEnum, params?: { [key: string]: string }): boolean {
        const nextPage = this.getNextPage(page)
        return this.goToPage(nextPage, params)
    }

    protected goToPage(page?: keyof PagesEnum, params?: { [key: string]: string }): boolean {
        if (page) {
            if (this.parentAlias) {
                Services.NavigationService.goToPage(
                    this.parentAlias as keyof BasePagesEnum,
                    params,
                    undefined,
                    page.toString()
                )
                return true
            } else {
                Services.NavigationService.goToPage(page as keyof BasePagesEnum, params)
                return true
            }
        }

        return false
    }

    // eslint-disable-next-line complexity -- TODO
    public async checkPage(
        page: keyof PagesEnum,
        validator: PageValidator<PagesEnum>,
        pathParams: { [key: string]: string }
    ): Promise<WorkflowResult> {
        logger.value.trace('checkPage')

        const pageIndex = this.pages.indexOf(page)
        const currentPageIndex = this.pages.indexOf(this.state.currentPage)

        logger.value.trace('checkPage, current page and page index are', { page, pageIndex })

        // If page is not part of the workflow, ignore all checks
        if (!this.inWorkflow(page)) {
            logger.value.debug('This page is not part of this workflow.', { page, pageIndex })
            return WorkflowResult.NotValid
        }

        // If we are going back in the workflow
        if (currentPageIndex > pageIndex) {
            if (validator.canBeSkippedOnBack && (await validator.canBeSkippedOnBack(this.state, pathParams))) {
                logger.value.debug('Page can be skipped on back action.', { page, pageIndex })
                return WorkflowResult.SkipBack
            }
        }

        // If we are going forward in the workflow
        if (currentPageIndex < pageIndex || currentPageIndex === 0) {
            if (validator.canBeSkipped && (await validator.canBeSkipped(this.state, pathParams))) {
                logger.value.debug('Page can be skipped.', { page, pageIndex })
                return WorkflowResult.SkipForward
            }
        }

        if (await validator.validate(this.state, pathParams)) {
            // Update state to this page
            this.state.currentPage = page
            setSessionStorage(this.stateStorageKey, this.state)

            logger.value.info(`Workflow page ${page.toString()} is valid. Workflow state updated in local storage.`, {
                localStorageKey: this.stateStorageKey,
                state: this.state,
            })

            return WorkflowResult.Ok
        }

        logger.value.debug('Page is not valid.', { page, pageIndex })
        if (pageIndex === 0) {
            return WorkflowResult.NotValid
        } else return WorkflowResult.SkipBack
    }

    public end() {
        logger.value.debug('Ending workflow', this.stateStorageKey)
        removeSessionStorage(this.stateStorageKey)
    }

    public abstract updateState(updateState: (state: WorkflowState<PagesEnum>) => WorkflowState<PagesEnum>): void

    public abstract inExactWorkflow(page: keyof PagesEnum, pathParams: { [key: string]: string }): boolean

    public abstract navigateBackToWorkflow(): boolean
}
